/**
 * useFeature — fail-closed release-toggle hook.
 *
 * Reads the server-resolved enabled set bootstrapped in `releaseFeatures.ts`
 * (CHG-001) and returns `true` ONLY when the key is present in a successfully
 * resolved set. Unknown/unconfigured key, not-yet-bootstrapped state, or a
 * failed bootstrap all return `false` (fail-closed,
 * SPEC-FEATURE-TOGGLES-RULE-001/006, SCENARIO-001/004).
 *
 * This hook is a PURE consumer of the server set — it performs NO client-side
 * flag re-evaluation (SPEC-FEATURE-TOGGLES-RULE-002). It subscribes to the
 * store via `useSyncExternalStore` so a feature that resolves enabled after the
 * loading state re-renders consumers without a manual refresh.
 *
 * @module feature-gate/useFeature
 * @see SPEC-FEATURE-TOGGLES
 */

import { useCallback, useSyncExternalStore } from "react";
import {
  type BootstrapStatus,
  getStatus,
  isEnabled,
  subscribe,
} from "./releaseFeatures";

/**
 * Returns whether the release toggle `key` is enabled in the bootstrapped set.
 * Fail-closed: `false` for unknown keys and for any non-ready bootstrap state.
 */
export function useFeature(key: string): boolean {
  const getSnapshot = useCallback(() => isEnabled(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Returns the current bootstrap status so loading/error UIs (GateLoader) can
 * react. Does NOT change fail-closed semantics — the enabled set stays empty in
 * `loading` and `error`.
 */
export function useReleaseFeaturesStatus(): BootstrapStatus {
  return useSyncExternalStore(subscribe, getStatus, getStatus);
}
